/**
 * Generated by scripts/design-system/regenerate-design-system.cjs.
 * Source spec hash: 935ffd335bc53337050549de2b401b55a2eef628d53f804744a978f0f88db128
 */
import cn from 'classnames'
import React, { ComponentProps, ReactElement } from 'react'

import Button from '../Button/Button'
import style from './DropdownButton.style'

type DropdownButtonTriggerProps = {
  'aria-controls'?: string
  'aria-expanded'?: boolean
  'aria-haspopup'?: 'menu'
  'data-dropdown-button-trigger'?: string
  onClick?: React.MouseEventHandler<HTMLElement>
  onMouseEnter?: React.MouseEventHandler<HTMLElement>
  onMouseLeave?: React.MouseEventHandler<HTMLElement>
}

type DropdownButtonCloseOptions = {
  returnFocus?: boolean
}

export type DropdownButtonProps = Omit<ComponentProps<'div'>, 'children' | 'onToggle'> & {
  children?: React.ReactNode
  className?: string
  closeOnSelect?: boolean
  defaultOpen?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  isOpen?: boolean
  menu: React.ReactNode
  onToggle?: (next: boolean) => void
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  trigger?: React.ReactNode
  triggerOn?: 'click' | 'hover'
  variant?: 'default' | 'secondary' | 'severe' | 'severeSecondary' | 'noShell' | 'inline' | 'inlineMuted' | 'inlineSevere'
}

function DropdownButton(props: DropdownButtonProps): ReactElement {
  const {
    children,
    className = '',
    closeOnSelect = true,
    defaultOpen = false,
    iconLeft,
    iconRight,
    isOpen,
    menu,
    onMouseEnter,
    onMouseLeave,
    onToggle,
    placement = 'bottom-start',
    trigger,
    triggerOn = 'click',
    variant = 'secondary',
    ...rest
  } = props
  const menuId = React.useId()
  const rootRef = React.useRef<HTMLDivElement>(null)
  const closeTimerRef = React.useRef<number | null>(null)
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
  const open = isOpen ?? uncontrolledOpen
  const openRef = React.useRef(open)

  React.useEffect(() => {
    openRef.current = open
  }, [open])

  const clearHoverCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current === null) {
      return
    }

    window.clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }, [])

  const focusTrigger = React.useCallback(() => {
    window.setTimeout(() => {
      rootRef.current
        ?.querySelector<HTMLElement>('[data-dropdown-button-trigger]')
        ?.focus()
    }, 0)
  }, [])

  const setOpenState = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen === openRef.current) {
        return
      }

      openRef.current = nextOpen

      if (isOpen === undefined) {
        setUncontrolledOpen(nextOpen)
      }

      onToggle?.(nextOpen)
    },
    [isOpen, onToggle],
  )

  const close = React.useCallback((options: DropdownButtonCloseOptions = {}) => {
    const { returnFocus = false } = options

    clearHoverCloseTimer()

    if (!openRef.current) {
      return
    }

    setOpenState(false)

    if (!returnFocus) {
      return
    }

    focusTrigger()
  }, [clearHoverCloseTimer, focusTrigger, setOpenState])

  const openFromHover = React.useCallback(() => {
    if (triggerOn !== 'hover') {
      return
    }

    clearHoverCloseTimer()
    setOpenState(true)
  }, [clearHoverCloseTimer, setOpenState, triggerOn])

  const queueHoverClose = React.useCallback(() => {
    if (triggerOn !== 'hover') {
      return
    }

    clearHoverCloseTimer()
    closeTimerRef.current = window.setTimeout(close, 100)
  }, [clearHoverCloseTimer, close, triggerOn])

  React.useEffect(() => clearHoverCloseTimer, [clearHoverCloseTimer])

  React.useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleOutsidePointerDown = (event: MouseEvent) => {
      const target = event.target

      if (!(target instanceof Node) || rootRef.current?.contains(target)) {
        return
      }

      close()
    }

    document.addEventListener('mousedown', handleOutsidePointerDown)

    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown)
    }
  }, [close, open])

  React.useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }

      event.preventDefault()
      close({ returnFocus: true })
    }

    document.addEventListener('keydown', handleEscapeKey)

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [close, open])

  const handleTriggerClick = React.useCallback(() => {
    clearHoverCloseTimer()
    setOpenState(!openRef.current)
  }, [clearHoverCloseTimer, setOpenState])

  const triggerProps = {
    'aria-controls': menuId,
    'aria-expanded': open,
    'aria-haspopup': 'menu' as const,
    'data-dropdown-button-trigger': '',
    onClick: handleTriggerClick,
    onMouseEnter: openFromHover,
    onMouseLeave: queueHoverClose,
  }

  const renderedTrigger = React.isValidElement<DropdownButtonTriggerProps>(trigger) ? (
    React.cloneElement(trigger, {
      ...triggerProps,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        trigger.props.onClick?.(event)

        if (!event.defaultPrevented) {
          handleTriggerClick()
        }
      },
      onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
        trigger.props.onMouseEnter?.(event)
        openFromHover()
      },
      onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
        trigger.props.onMouseLeave?.(event)
        queueHoverClose()
      },
    })
  ) : (
    <Button
      aria-controls={menuId}
      aria-expanded={open}
      aria-haspopup='menu'
      data-dropdown-button-trigger=''
      iconLeft={iconLeft}
      iconRight={iconRight}
      onClick={handleTriggerClick}
      onMouseEnter={openFromHover}
      onMouseLeave={queueHoverClose}
      type='button'
      variant={variant}
    >
      {children}
    </Button>
  )

  const handleMenuClick = React.useCallback(() => {
    if (closeOnSelect) {
      close({ returnFocus: true })
    }
  }, [close, closeOnSelect])

  const handleRootMouseEnter: React.MouseEventHandler<HTMLDivElement> = (event) => {
    onMouseEnter?.(event)
    openFromHover()
  }

  const handleRootMouseLeave: React.MouseEventHandler<HTMLDivElement> = (event) => {
    onMouseLeave?.(event)
    queueHoverClose()
  }

  return (
    <div
      {...rest}
      className={cn('DropdownButton', className, { open })}
      css={style}
      onMouseEnter={handleRootMouseEnter}
      onMouseLeave={handleRootMouseLeave}
      ref={rootRef}
      role='presentation'
    >
      {renderedTrigger}
      {open ? (
        <div
          className={cn('menu', placement)}
          id={menuId}
          onClick={handleMenuClick}
          onMouseEnter={openFromHover}
          onMouseLeave={queueHoverClose}
          role='presentation'
        >
          {menu}
        </div>
      ) : null}
    </div>
  )
}

export default DropdownButton
